/**
 * Pure routing/decision logic for `main.tsx`'s `AuthGate`, factored out so
 * the boot-gate screen selection is unit-testable in `bun:test` without
 * rendering React or touching the DOM (wagepro-web has no jsdom/render-test
 * infrastructure today — zero `*.test.tsx` files, see `auth_context.tsx`'s
 * module docblock for the same "why" applied to `resolveAuthBoot`).
 *
 * Added as part of PR #22341 adversarial review findings #1/#2: the ordering
 * of `AuthGate`'s early-return `if` chain is exactly what made the
 * `grant_error` `AuthStatus` (see `auth_context.tsx`) unreachable — a
 * `status !== 'authenticated'` guard caught it before any branch that would
 * render a real screen, so a transient grant-fetch failure rendered a
 * permanently blank page. Extracting the ordering into a pure function that
 * returns a plain discriminated string means a regression in that ordering
 * is a one-line assertion away from being caught, instead of only visible by
 * actually booting the app in a browser.
 */
import type { AuthStatus, SessionKind } from './auth_context.js';
import { REAUTHENTICATE_PROMPT } from './oauth_pkce.js';
import type { SessionFactsResult } from '../../data/use_session_facts.js';

export type AuthGateScreen =
  | 'boot_placeholder'
  | 'grant_error'
  | 'signed_out'
  | 'no_payroll_access'
  | 'session_error'
  | 'app';

/**
 * Mirrors `AuthGate`'s `if` chain exactly, in the same order. `grant_error`
 * is checked FIRST, before the blanket `status !== 'authenticated'`
 * fallthrough — that ordering is the entire fix for findings #1/#2, so it is
 * deliberately the first branch here too, not merely tested as an
 * afterthought. `signed_out` sits immediately after it for the identical
 * reason: it is a real, user-reachable screen whose status is not
 * `authenticated`, so the fallthrough would render it as a blank page.
 *
 * `settled` (the former `useDevBackendData` boot-time gate) is GONE as of
 * C12 (#22334) — the dev-bypass data fan-out it waited on had zero fan-out
 * targets left (every real target was migrated to its own self-fetching
 * hook by C1-C11) and its resolved value was already discarded. Removing it
 * only drops one extra `boot_placeholder` paint on the FIRST `authenticated`
 * render (it was `useState(false)`, flipped `true` from a passive
 * `useEffect`, so that first render always saw it `false`) — safe because
 * `resolveAuthBoot` (`auth_context.tsx`) never returns `status:
 * 'authenticated'` with a null/undefined grant; a second consecutive
 * non-auth grant-fetch failure surfaces the distinct `grant_error` status
 * instead (see that function's docblock, step 4). That invariant is what
 * matters here: `sessionFactsStatus === 'no_entity'` is checked BELOW,
 * before the `loading` fallback, so an `authenticated` render with an
 * unstable/absent entity context could otherwise flash a spurious
 * `no_payroll_access`/`session_error` for that one extra frame `settled`
 * used to buy.
 */
export function resolveAuthGateScreen(input: {
  status: AuthStatus;
  sessionFactsStatus: SessionFactsResult['status'];
  /**
   * Whether the automatic post-sign-out redirect to Ledger's sign-in page
   * (see {@link resolveAutoSignInPrompt}) failed to even start — the only
   * case that still renders the `signed_out` card. `false`/absent means the
   * redirect is in flight, so the neutral placeholder shows for the frame or
   * two before the browser leaves the page, rather than a card the user has
   * to read and dismiss on the way to the same destination.
   */
  signOutRedirectFailed?: boolean;
  /**
   * Which session shape booted (`auth_context.tsx`). An `employee`
   * self-service session is checked AFTER the `authenticated` fallthrough but
   * BEFORE every `sessionFactsStatus` branch, because it can never satisfy
   * any of them — see the branch below.
   */
  sessionKind?: SessionKind | null;
}): AuthGateScreen {
  if (input.status === 'grant_error') {
    return 'grant_error';
  }

  // Same class of ordering bug as `grant_error` (findings #1/#2): a
  // deliberate sign-out is not `authenticated`, so the blanket fallthrough
  // below would swallow it into a blank `boot_placeholder`. It is still
  // checked before that fallthrough — but now only the FAILED-redirect case
  // renders the card. The happy path is a straight redirect to Ledger's
  // sign-in page, so the card would just be a speed bump in front of it.
  if (input.status === 'signed_out') {
    return input.signOutRedirectFailed === true
      ? 'signed_out'
      : 'boot_placeholder';
  }

  // `needs_ledger_sso` — a cookie identity resolved but neither a WagePro
  // OAuth grant nor an active ESS link matched (see {@link AuthStatus} in
  // `auth_context.tsx`). The AuthGate redirect effect sends this to
  // `buildLedgerSsoLoginPath()`, but the paint IS a neutral placeholder for
  // the frames between resolution and navigation — same shape as the
  // `signed_out` happy path above.
  if (input.status === 'needs_ledger_sso') {
    return 'boot_placeholder';
  }

  if (input.status !== 'authenticated') {
    return 'boot_placeholder';
  }

  // A self-service (employee) session deliberately never resolves employer
  // session facts: its grant carries `firmId: null`, so `useOAuthEntityContext`
  // reports `no_entity` and `useSessionFacts` never fires `entityUser.me` /
  // `entity.clientGet` (both of which an employee is forbidden from calling —
  // they have no entity membership, and `clientGet` is employer-scoped). Every
  // branch below would therefore misread that deliberate `no_entity` as a
  // failure and show `no_payroll_access` to someone whose payroll access is
  // precisely what just got granted. `main.tsx` supplies this session's facts
  // from the boot's own `EmployeeSelfContext` instead.
  if (input.sessionKind === 'employee') {
    return 'app';
  }

  if (input.sessionFactsStatus === 'no_entity') {
    return 'no_payroll_access';
  }

  if (input.sessionFactsStatus === 'error') {
    return 'session_error';
  }

  if (input.sessionFactsStatus === 'loading') {
    return 'boot_placeholder';
  }

  return 'app';
}

/**
 * The OIDC `prompt` values `AuthGate` should eventually send on a
 * `beginSignIn()` authorize request for this `AuthStatus` — or `null` when
 * it must not auto-redirect at all. Extracted from what used to be an
 * inline `status === 'unauthenticated'` effect condition for the same reason
 * `resolveAuthGateScreen` exists: the rule it encodes is invisible to
 * TypeScript and was only observable by actually booting the app.
 *
 * As of the WagePro-branded `/login` screen, `AuthGate`'s effect no longer
 * calls `beginSignIn()` with this return value directly — it navigates to
 * `buildLoginPath({ forceReauth: prompt.length > 0 })`
 * (`wagepro_login_logic.ts`) instead, so the OAuth handoff happens from a
 * WagePro-branded page rather than bouncing straight through Ledger's
 * generic sign-in page. The `unauthenticated`/`signed_out` distinction this
 * function encodes is preserved (empty array vs. `REAUTHENTICATE_PROMPT`,
 * discriminated by length at the call site) — it now selects whether that
 * navigation carries `FORCE_REAUTH_PARAM`, not whether the OIDC request
 * itself carries `prompt=login`. See that constant's docblock for why the
 * guarantee `prompt=login` originally provided is preserved structurally
 * once the redirect lands on a screen that always requires a fresh
 * credential submission, rather than by propagating the OIDC param further.
 *
 * The rule, and why each case differs:
 *
 * - `unauthenticated` — a cold boot with no usable token. No prompt: if the
 *   Ledger session is still good, going straight back in (consent screen or
 *   auto-approve) is exactly what the user wants from a relaunch.
 * - `signed_out` — the user DELIBERATELY signed out, so `REAUTHENTICATE_PROMPT`
 *   (`prompt=login`) is mandatory. Without it, `GET /oauth/authorize` answers
 *   from the Ledger session cookie that `signOut()` cannot end (it is
 *   host-scoped and shared with every other first-party client) and silently
 *   re-mints a token — which is what made "Sign out" look like a no-op and
 *   forced the interstitial card this predicate previously existed to gate.
 *   With the prompt, the redirect lands on Ledger's real sign-in page, so it
 *   is a correct and visible destination to send the user to immediately.
 * - anything else — `error` would recreate its own error and loop;
 *   `grant_error`, `loading` and `authenticated` have their own screens.
 *
 * Returning an empty array (`unauthenticated`) is NOT the same as returning
 * `null` (do not redirect): callers must discriminate on `=== null`, never on
 * truthiness or `.length`.
 */
export function resolveAutoSignInPrompt(
  status: AuthStatus,
): readonly string[] | null {
  if (status === 'unauthenticated') return [];
  if (status === 'signed_out') return REAUTHENTICATE_PROMPT;
  return null;
}

/**
 * Discriminates WHICH login destination `AuthGate`'s redirect effect should
 * navigate to for a given {@link AuthStatus}.
 *
 * - `null` — do not redirect (`loading`/`authenticated`/`error`/`grant_error`).
 * - `'ledger_sso'` — go to `buildLedgerSsoLoginPath()`, i.e. the launcher-
 *   style entry that starts the "Sign in with Ledger" PKCE handoff on
 *   mount. Reached for {@link AuthStatus} `'needs_ledger_sso'` — a Ledger-
 *   first admin whose cookie identity resolved but whose WagePro consent
 *   isn't (or is no longer) held. `wageproAuth.login` would 403 for these
 *   users, so the plain credentials form is the wrong destination.
 * - `{ forceReauth: boolean }` — go to `buildLoginPath({ forceReauth })`,
 *   the WagePro-branded email/password form. `forceReauth: true` on
 *   `signed_out` so the login screen can show a "you were signed out"
 *   message; `forceReauth: false` on `unauthenticated` (cold boot).
 *
 * A single decision function so the AuthGate redirect effect never
 * hand-writes this branching — the pattern that made #22341 findings #1/#2
 * possible in `resolveAuthGateScreen` above.
 */
export type AuthGateRedirect = 'ledger_sso' | { forceReauth: boolean } | null;

export function resolveAuthGateRedirect(status: AuthStatus): AuthGateRedirect {
  if (status === 'needs_ledger_sso') return 'ledger_sso';
  const prompt = resolveAutoSignInPrompt(status);
  if (prompt === null) return null;
  return { forceReauth: prompt.length > 0 };
}
